import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getProgrammeById, deleteProgramme, ProgrammeReadDetailed } from '../api/programmes';
import { getProjects } from '../api/projects';
import { Project } from '../types/project';
import ProgrammeForm from '../components/ProgrammeForm';
import DeleteProgrammeModal from '../components/DeleteProgrammeModal';
import Button from '../components/Button';
import Badge from '../components/Badge';
import EntityDetailLayout from '../components/detail/EntityDetailLayout';
import DetailChildList, { DetailRow } from '../components/detail/DetailChildList';

const formatCurrency = (amount?: number): string => (amount ? `R${amount.toLocaleString()}` : 'R0');

const ProgrammeDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [programme, setProgramme] = useState<ProgrammeReadDetailed | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const [programmeData, projectsData] = await Promise.all([
        getProgrammeById(id),
        getProjects(id),
      ]);
      setProgramme(programmeData);
      setProjects(projectsData);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load programme');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDeleteConfirm = async () => {
    if (!id) return;
    try {
      await deleteProgramme(id);
      navigate('/programmes');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete programme');
      setDeleting(false);
    }
  };

  const projectRows: DetailRow[] = projects.map((project) => ({
    id: project.id,
    to: `/projects/${project.id}`,
    title: project.name,
    subtitle: project.description ?? undefined,
    badges: project.status ? (
      <Badge status={project.status as any} size="sm">
        {project.status}
      </Badge>
    ) : undefined,
    meta: project.budget ? formatCurrency(project.budget) : undefined,
  }));

  return (
    <>
      <EntityDetailLayout
        breadcrumb={[
          { label: 'Programmes', path: '/programmes' },
          { label: programme?.name ?? 'Programme' },
        ]}
        loading={loading}
        error={error}
        title={programme?.name ?? ''}
        subtitle={programme?.description ?? undefined}
        metrics={
          programme
            ? [
                { label: 'Total Projects', value: programme.total_projects ?? 0, accent: 'blue' },
                { label: 'Total Budget', value: formatCurrency(programme.total_budget), accent: 'green' },
                { label: 'Total Tasks', value: programme.total_tasks ?? 0, accent: 'purple' },
                { label: 'Completed Tasks', value: programme.completed_tasks ?? 0, accent: 'amber' },
              ]
            : undefined
        }
        progress={programme ? { percentage: programme.completion_percentage ?? 0 } : undefined}
        actions={
          programme ? (
            <>
              <Button variant="primary" size="sm" onClick={() => setEditing(true)}>
                Edit
              </Button>
              <Button variant="danger" size="sm" onClick={() => setDeleting(true)}>
                Delete
              </Button>
            </>
          ) : undefined
        }
      >
        <DetailChildList
          title="Projects"
          rows={projectRows}
          emptyMessage="No projects in this programme yet."
        />
      </EntityDetailLayout>

      {editing && programme && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="w-full max-w-2xl my-8">
            <ProgrammeForm
              programme={programme}
              onSuccess={() => {
                setEditing(false);
                load();
              }}
              onCancel={() => setEditing(false)}
            />
          </div>
        </div>
      )}

      {deleting && programme && (
        <DeleteProgrammeModal
          programmeName={programme.name}
          projectCount={programme.total_projects || 0}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleting(false)}
        />
      )}
    </>
  );
};

export default ProgrammeDetailPage;
